import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftOutlined, LoadingOutlined } from "@ant-design/icons";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";
import { useTranslation } from "../../i18n/useTranslation";

export default function PhoneStep({
  phone,
  setPhone,
  onSendOtp,
  onClose,
  loading = false,
  serverError = "",
  clearServerError,
  smsCooldown = 0,
}) {
  const { t } = useTranslation();
  const [error, setError] = useState("");

  const ALLOWED_COUNTRIES = [
    "FR",
    "BE",
    "NL",
    "LU",
    "ES",
    "PT",
    "IT",
    "DE",
    "GB",
    "JE", 
    "GG", 
    "IM", 
    "US",
    "CA",
    "JP",
    "KR",
    "GA",
    "CI",
    "GH",
    "KE",
    "NG",
    "ZA",
  ];

  // Fallback : certaines plages +44 ont country=undefined dans libphonenumber-js
  const CALLING_CODE_FALLBACK = {
    "44": "GB",
    "1": "US",
  };

  const formatPhone = (value) => {
    return new AsYouType().input(value || "");
  };

  const toE164 = (value) => {
    try {
      const phoneNumber = parsePhoneNumberFromString(value || "");

      if (!phoneNumber || !phoneNumber.isPossible()) {
        return null;
      }

      const effectiveCountry =
        phoneNumber.country ??
        CALLING_CODE_FALLBACK[phoneNumber.countryCallingCode];

      if (!effectiveCountry || !ALLOWED_COUNTRIES.includes(effectiveCountry)) {
        return "UNSUPPORTED_COUNTRY";
      }

      return phoneNumber.number;
    } catch {
      return null;
    }
  };

  const getPhoneError = (value) => {
    if (!value?.trim()) return "";

    const result = toE164(value);

    if (result === "UNSUPPORTED_COUNTRY") {
      return "Pays non supporté";
    }

    if (!result) {
      return "Numéro invalide";
    }

    return "";
  };

  const handleContinue = async () => {
    if (loading || smsCooldown > 0) return;

    const validationError = getPhoneError(phone);

    if (validationError) {
      setError(validationError);
      return;
    }

    const e164 = toE164(phone);

    if (!e164 || e164 === "UNSUPPORTED_COUNTRY") {
      setError("Numéro invalide");
      return;
    }

    setError("");
    if (clearServerError) clearServerError();

    try {
      await onSendOtp(e164);
    } catch {
      setError("Impossible d'envoyer le SMS. Réessaie.");
    }
  };

  const displayedError = error || serverError;

  return (
    <>
      <div className="modal-text-container modal-container">
        <ArrowLeftOutlined className="auth-back" onClick={onClose} />

        <div className="modal-header-container">
          <p className="modal-text-header">{t("phone_login_title")}</p>
        </div>

        <p className="auth-helper">{t("phone_login_subtitle")}</p>

        <input
          className="auth-input"
          value={phone}
          onChange={(e) => {
            const formatted = formatPhone(e.target.value);
            setPhone(formatted);
            setError(getPhoneError(formatted));
            if (clearServerError) clearServerError();
          }}
          placeholder="+33 6 12 34 56 78"
          inputMode="tel"
          autoComplete="tel"
        />

        {displayedError ? (
          <p className="auth-error">{displayedError}</p>
        ) : null}
      </div>

      <div className="modal-close-container">
        <p
          className="auth-helper"
          style={{
            marginBottom: 16,
            fontSize: 13,
            lineHeight: 1.45,
            textAlign: "center",
          }}
        >
          En continuant, vous acceptez les{" "}
          <Link to="/terms-of-service" className="legal">
            Conditions d’utilisation
          </Link>{" "}
          et la{" "}
          <Link to="/privacy-policy" className="legal">
            Politique de confidentialité
          </Link>
          .
        </p>

        <div
          className="modal-close-button"
          onClick={handleContinue}
          style={{
            pointerEvents: loading || smsCooldown > 0 ? "none" : "auto",
          }}
        >
          {loading ? (
            <LoadingOutlined spin />
          ) : smsCooldown > 0 ? (
            `Renvoyer dans ${smsCooldown}s`
          ) : (
            t("continue")
          )}
        </div>
      </div>
    </>
  );
}